/**
 * 사이드바 설정 파일
 * 메뉴 구조와 아이콘 매핑을 중앙 관리
 */

import React from 'react';
import { matchPath } from 'react-router-dom';
import {
  FnsIcon,
  HomeIcon,
  ProjectIcon,
  HRupIcon,
  SidebarApprovalIcon,
  SidebarGoalsIcon,
} from '@/shared/ui/Icons/Icons';
import type { MenuItem, SidebarConfig } from './types';

// 기존 타입을 types.ts로 이동했으므로 재export
export type { MenuItem, SidebarConfig } from './types';

// 타입 안전성 강화 (메뉴 아이템 id 기준)
export type IconType = 'home' | 'projects' | 'approval' | 'boards' | 'goals' | 'fns' | 'hrup';

export const sidebarConfig: SidebarConfig = {
  // 아이콘 매핑 (중앙 관리)
  icons: {
    project: 'ProjectIcon',
    home: 'HomeIcon',
    default: '📄',
  },

  // 메뉴 구조 (아이콘 및 특별 버튼 설정 추가)
  menuItems: [
    {
      id: 'home',
      label: 'HOME',
      icon: '🏠',
      defaultPath: '/home',
    },
    // 1. 프로젝트
    {
      id: 'projects',
      label: '프로젝트',
      icon: '📁',
      defaultPath: '/projects',
      children: [
        { id: 'project-list', label: '부서별 프로젝트', path: '/projects' },
        { id: 'project-numbers', label: '전사 프로젝트', path: '/projects/numbers' },
      ],
    },
    // 2. 목표관리
    {
      id: 'goals',
      label: '목표관리',
      icon: '🎯',
      defaultPath: '/goals/company-goal',
      children: [
        { id: 'company-goal', label: '전사 매출현황', path: '/goals/company-goal' },
        {
          id: 'department-financial-goals',
          label: '부서 목표관리',
          path: '/goals/department-financial-goals',
        },
        { id: 'personal-goal', label: '프로젝트 기여도', path: '/goals/personal-goal' },
      ],
    },
    // 3. 전자결재
    {
      id: 'approval',
      label: '전자결재',
      icon: '📝',
      defaultPath: '/approval/home',
      children: [
        { id: 'approval-home', label: '전자결재 홈', path: '/approval/home' },
        { id: 'approval-action', label: '결재하기', path: '/approval/pending' },
        { id: 'approval-documents-all', label: '전체 문서함', path: '/approval/documents/all' },
        {
          id: 'approval-documents-temporary',
          label: '임시 저장함',
          path: '/approval/documents/temporary',
        },
        { id: 'approval-draft', label: '기안하기', path: '/approval/draft', hasPopupIcon: true },
      ],
    },
    // 4. 게시판
    {
      id: 'boards',
      label: '게시판',
      icon: '📋',
      defaultPath: '/boards',
      children: [
        { id: 'boards-home', label: 'New', path: '/boards' },
        { id: 'boards-di-talk', label: 'DI TALK', path: '/boards/di-talk' },
        { id: 'boards-notice', label: '공지사항', path: '/boards/notice' },
        { id: 'boards-library', label: '자료실', path: '/boards/library' },
      ],
    },
    // 5. F&S
    {
      id: 'fns',
      label: 'F&S',
      icon: '📊',
      defaultPath: '/fns/accounting-number',
      children: [
        { id: 'fns-accounting', label: '회계넘버 관리', path: '/fns/accounting-number' },
        { id: 'fns-goals', label: '재무목표 관리', path: '/fns/financial-goals' },
        { id: 'fns-payment-status', label: '외주비 현황', path: '/fns/payment-status' },
        { id: 'fns-cash-flows', label: '수금/지급 현황', path: '/fns/cash-flows' },
      ],
    },
    // 6. HRup
    // 접근 제어는 useFilteredMenuItems 에서 actions(hrup.*) 기반으로 처리
    {
      id: 'hrup',
      label: 'HRup',
      icon: '🏢',
      defaultPath: '/hrup/personnel',
      children: [
        { id: 'hrup-personnel', label: '인사관리', path: '/hrup/personnel' },
        { id: 'hrup-departments', label: '부서관리', path: '/hrup/departments' },
        { id: 'hrup-approval-line', label: '결재선 관리', path: '/hrup/approval-line' },
        { id: 'hrup-schedule', label: '일정관리', path: '/hrup/schedule' },
        { id: 'hrup-history', label: '변경 이력', path: '/hrup/history' },
      ],
    },
  ],
};

export interface RouteMeta {
  path: string;
  label: string;
  itemId: string;
  topMenuId: string;
}

const collectRouteMeta = (
  items: readonly MenuItem[],
  topMenuId?: string,
): Record<string, RouteMeta> => {
  const map: Record<string, RouteMeta> = {};

  items.forEach((item) => {
    const currentTop = topMenuId ?? item.id;

    if (item.path) {
      map[item.path] = {
        path: item.path,
        label: item.label,
        itemId: item.id,
        topMenuId: currentTop,
      };
    }

    if (item.children) {
      Object.assign(map, collectRouteMeta(item.children, currentTop));
    }
  });

  return map;
};

const menuRouteMetaMap = collectRouteMeta(sidebarConfig.menuItems);

const extraRouteMeta: RouteMeta[] = [
  // 홈 직접 URL(/home) 헤더 타이틀
  {
    path: '/home',
    label: 'HOME',
    itemId: 'home',
    topMenuId: 'home',
  },
  {
    path: '/approval',
    label: '전자결재',
    itemId: 'approval-home',
    topMenuId: 'approval',
  },
  {
    path: '/approval/home',
    label: '전자결재',
    itemId: 'approval-home',
    topMenuId: 'approval',
  },
  {
    path: '/approval/draft',
    label: '기안하기',
    itemId: 'approval-draft',
    topMenuId: 'approval',
  },
  {
    path: '/projects/:id',
    label: '프로젝트 상세',
    itemId: 'project-detail',
    topMenuId: 'projects',
  },
  {
    path: '/approval/pending/:documentId',
    label: '전자결재',
    itemId: 'approval-document-detail',
    topMenuId: 'approval',
  },
  {
    path: '/approval/draft/pt-estimate',
    label: 'PT 견적서 기안하기',
    itemId: 'approval-draft-pt-estimate',
    topMenuId: 'approval',
  },
  {
    path: '/approval/draft/pre-order-review',
    label: '사전발주검토서 기안하기',
    itemId: 'approval-draft-pre-order-review',
    topMenuId: 'approval',
  },
  {
    path: '/approval/draft/post-settlement',
    label: '사후정산서 기안하기',
    itemId: 'approval-draft-post-settlement',
    topMenuId: 'approval',
  },
  {
    path: '/approval/draft/outsourcing-payment',
    label: '외주비 지급요청서 기안하기',
    itemId: 'approval-draft-outsourcing-payment',
    topMenuId: 'approval',
  },
  {
    path: '/approval/draft/labor-payment',
    label: '인건비 지급요청서 기안하기',
    itemId: 'approval-draft-labor-payment',
    topMenuId: 'approval',
  },
  {
    path: '/approval/draft/operating-expense',
    label: '진행비 품의서 기안하기',
    itemId: 'approval-draft-operating-expense',
    topMenuId: 'approval',
  },
  {
    path: '/approval/draft/travel-request',
    label: '출장품의서 기안하기',
    itemId: 'approval-draft-travel-request',
    topMenuId: 'approval',
  },
  {
    path: '/approval/draft/business-draft',
    label: '업무기안 기안하기',
    itemId: 'approval-draft-business-draft',
    topMenuId: 'approval',
  },
  {
    path: '/approval/draft/purchase-request',
    label: '구매요청서(물품 및 소프트웨어) 기안하기',
    itemId: 'approval-draft-purchase-request',
    topMenuId: 'approval',
  },
  {
    path: '/approval/draft/corporate-card-personal',
    label: '법인카드 정산서 기안하기',
    itemId: 'approval-draft-corporate-card-personal',
    topMenuId: 'approval',
  },
  {
    path: '/approval/draft/corporate-card-shared',
    label: '법인카드정산서(공용) 기안하기',
    itemId: 'approval-draft-corporate-card-shared',
    topMenuId: 'approval',
  },
  {
    path: '/approval/draft/personal-card',
    label: '개인카드 정산서 기안하기',
    itemId: 'approval-draft-personal-card',
    topMenuId: 'approval',
  },
  {
    path: '/approval/draft/corporate-seal',
    label: '법인인감 사용 신청서 기안하기',
    itemId: 'approval-draft-corporate-seal',
    topMenuId: 'approval',
  },
  {
    path: '/approval/draft/cash-collection-report',
    label: '수금지급현황보고 기안하기',
    itemId: 'approval-draft-cash-collection-report',
    topMenuId: 'approval',
  },
  {
    path: '/approval/draft/customer-registration',
    label: '고객사 등록 품의(신규/변경) 기안하기',
    itemId: 'approval-draft-customer-registration',
    topMenuId: 'approval',
  },
  {
    path: '/approval/draft/severance-payment',
    label: '퇴직금 품의서 기안하기',
    itemId: 'approval-draft-severance-payment',
    topMenuId: 'approval',
  },
  {
    path: '/approval/documents/:documentId',
    label: '전자결재',
    itemId: 'approval-document-detail-view',
    topMenuId: 'approval',
  },
  // HRup 라우트 메타
  {
    path: '/hrup/personnel/resigned',
    label: '퇴사자 목록',
    itemId: 'hrup-personnel-resigned',
    topMenuId: 'hrup',
  },
  {
    path: '/hrup/personnel/department-moves',
    label: '발령 이력',
    itemId: 'hrup-personnel-department-moves',
    topMenuId: 'hrup',
  },
  {
    path: '/boards/:boardType/:id',
    label: '게시글 상세',
    itemId: 'boards-detail',
    topMenuId: 'boards',
  },
  {
    path: '/boards/:boardType/write',
    label: '게시글 작성',
    itemId: 'boards-write',
    topMenuId: 'boards',
  },
  {
    path: '/boards/:boardType/write/:id',
    label: '게시글 수정',
    itemId: 'boards-edit',
    topMenuId: 'boards',
  },
  {
    path: '/hrup/approval-line',
    label: '결재선 관리',
    itemId: 'hrup-approval-line',
    topMenuId: 'hrup',
  },
  {
    path: '/hrup/schedule',
    label: '일정관리',
    itemId: 'hrup-schedule',
    topMenuId: 'hrup',
  },
];

const extraRouteMetaMap = Object.fromEntries(extraRouteMeta.map((meta) => [meta.path, meta]));

export const routeMetaMap: Record<string, RouteMeta> = {
  ...menuRouteMetaMap,
  ...extraRouteMetaMap,
};
export const labelByPath: Record<string, string> = Object.fromEntries(
  Object.entries(routeMetaMap).map(([path, meta]) => [path, meta.label]),
);

/**
 * pathname 으로 페이지 제목을 조회한다 (사이드바/모바일 GNB 헤더 공용).
 * 1. labelByPath 정확 매칭
 * 2. routeMetaMap 의 `:param` 패턴 매칭 (예: /projects/:id)
 * 매칭되는 값이 없으면 undefined — 기본 제목 처리는 호출부 책임.
 */
export const findPageTitleByPathname = (pathname: string): string | undefined => {
  const exactTitle = labelByPath[pathname];
  if (exactTitle) return exactTitle;

  for (const [pattern, meta] of Object.entries(routeMetaMap)) {
    if (!pattern.includes(':')) continue;

    const match = matchPath({ path: pattern, end: true }, pathname);
    if (match) return meta.label;
  }

  return undefined;
};

// 아이콘 헬퍼 함수 (에러 처리 및 성능 최적화)
// 타입 안전한 아이콘 매핑 (메뉴 아이템 id 기준)
const iconMap: Record<IconType, React.ComponentType<{ size?: number; 'aria-hidden'?: boolean }>> = {
  home: HomeIcon,
  projects: ProjectIcon,
  approval: SidebarApprovalIcon,
  boards: ProjectIcon, // TODO: 게시판 전용 아이콘 추가 후 교체
  goals: SidebarGoalsIcon,
  fns: FnsIcon,
  hrup: HRupIcon,
};

export const getMenuIcon = (menuId?: string): React.ReactNode => {
  if (!menuId) return null;
  const IconComponent = iconMap[menuId as IconType];
  if (!IconComponent) return null;
  return React.createElement(IconComponent, { size: 14, 'aria-hidden': true });
};
